import { Container, Card, Button, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faBox, faClock, faMapMarkerAlt, faPhone, faCreditCard } from '@fortawesome/free-solid-svg-icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import './pages.css';

const OrderSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  
  // Get order data from location state
  const order = location.state?.order;
  const message = location.state?.message || 'Order placed successfully!';

  // 6-second loading delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 6000); // 6 seconds

    return () => clearTimeout(timer); // Cleanup timer
  }, []);

  // Show loading spinner for 6 seconds
  if (isLoading) {
    return (
      <Container className="my-5">
        <Row className="justify-content-center">
          <Col lg={6} className="text-center">
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-5">
                <Spinner animation="border" variant="primary" size="lg" />
                <h4 className="mt-4 text-primary">Preparing your order confirmation...</h4>
                <p className="text-muted mt-2">
                  Please wait while we process your order details
                </p>
                <div className="mt-3">
                  <small className="text-muted">This may take a few moments</small>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  if (!order) {
    return (
      <Container className="my-5">
        <Alert variant="warning" className="text-center">
          <h4>No order information found</h4>
          <Button variant="danger" onClick={() => navigate('/')}>
            Back to Home
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="my-5">
      <Row className="justify-content-center">
        <Col lg={8}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center p-4 p-md-5">
              {/* Success Icon */}
              <div className="text-success mb-4">
                <FontAwesomeIcon icon={faCheckCircle} size="4x" />
              </div>
              
              <h1 className="mb-3">Order Confirmed!</h1>
              <p className="lead mb-4">
                {message} Order #{order._id.slice(-10).toUpperCase()}
              </p>
              
              {/* Order Summary */}
              <Card className="mb-4">
                <Card.Body>
                  <h5 className="mb-3">
                    <FontAwesomeIcon icon={faBox} className="me-2 text-muted" />
                    Order Summary
                  </h5>
                  
                  {order.items.map((item, index) => (
                    <div key={index} className="d-flex justify-content-between py-2 border-bottom">
                      <div>
                        <span className="fw-bold">{item.name}</span>
                        <span className="text-muted ms-2">× {item.quantity}</span>
                      </div>
                      <div>₦{(item.price * item.quantity).toLocaleString()}</div>
                    </div>
                  ))}
                  
                  <div className="d-flex justify-content-between mt-3 pt-2 fw-bold">
                    <span>Total</span>
                    <span>₦{order.total.toLocaleString()}</span>
                  </div>
                </Card.Body>
              </Card>
              
              {/* Delivery Information */}
              <Card className="mb-4">
                <Card.Body>
                  <h5 className="mb-3">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2 text-muted" />
                    Delivery Information
                  </h5>
                  
                  <div className="text-start">
                    <p className="mb-1">
                      <strong>Name:</strong> {order.customerName}
                    </p>
                    <p className="mb-1">
                      <strong>Address:</strong> {order.shippingAddress}
                    </p>
                    <p className="mb-1">
                      <strong>Phone:</strong> {order.phone}
                    </p>
                    <p className="mb-1">
                      <strong>Email:</strong> {order.email}
                    </p>
                    <p className="mb-0">
                      <FontAwesomeIcon icon={faClock} className="me-2 text-muted" />
                      Status: {order.status || 'Preparing your order'}
                    </p>
                  </div>
                </Card.Body>
              </Card>
              
              {/* Payment Method */}
              <Alert variant="light" className="text-start">
                <h5 className="mb-3">
                  <FontAwesomeIcon icon={faCreditCard} className="me-2 text-muted" />
                  Payment Method
                </h5>
                <p className="mb-0">
                  {order.paymentMethod === 'card' && 'Credit/Debit Card'}
                  {order.paymentMethod === 'transfer' && 'Bank Transfer'}
                  {order.paymentMethod === 'cash_on_delivery' && 'Cash on Delivery'}
                  {!['card', 'transfer', 'cash_on_delivery'].includes(order.paymentMethod) && order.paymentMethod}
                </p>
                {order.paymentMethod === 'transfer' && (
                  <div className="mt-3 p-3 bg-white rounded">
                    <h6>Bank Details</h6>
                    <p className="mb-1"><strong>Bank:</strong> GTBank</p>
                    <p className="mb-1"><strong>Account Name:</strong> Noureesh Foods</p>
                    <p className="mb-1"><strong>Account Number:</strong> 0123456789</p>
                    <p className="mb-0"><small>Reference: Order #{order._id.slice(-10).toUpperCase()}</small></p>
                  </div>
                )}
              </Alert>
              
              {/* Next Steps */}
              <div className="mt-4">
                <p className="mb-3">
                  We'll contact you when your order is on its way. 
                  For any questions, call us at <strong><FontAwesomeIcon icon={faPhone} /> +234 700 NOUREESH</strong>
                </p>
                
                <div className="d-flex flex-column flex-md-row justify-content-center gap-3">
                  <Button 
                    variant="danger" 
                    size="lg" 
                    className="px-4"
                    onClick={() => navigate('/menu')}
                  >
                    Order Again
                  </Button>
                  <Button 
                    variant="outline-secondary" 
                    size="lg" 
                    className="px-4"
                    onClick={() => navigate('/')}
                  >
                    Back to Home
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default OrderSuccess;
